import { useAuth } from '../auth/AuthProvider'
import { GlassCard, GlassPill, GlassScene } from '../shared/ui'

/**
 * Liquid Glass login page (DESIGN.md §14) — two entries, matching the M26
 * mockup "1 · Login" (Benutzerzugang/mockups/Main.dc.html):
 *   • Mitarbeiter-Login (primary, first) → chip or school card at /mitarbeiter/anmelden
 *   • Verwaltung & Schulen (quieter, second) → Entra SSO
 * The staff CTA is a solid primary pill by explicit owner request (mockup
 * fidelity) — the prominent action for viewers who never have an Entra session.
 */
export default function LoginPage() {
  const { login, isLoading } = useAuth()

  return (
    <div className="min-h-screen relative overflow-hidden">
      <GlassScene />

      <main className="relative z-10 min-h-screen flex items-center justify-center px-6 py-10">
        <div className="w-full max-w-md">
          {/* Wordmark + subtitle */}
          <div className="flex flex-col items-center mb-7">
            <img
              src="/mammascanteen_logo.svg"
              alt="Mammas Canteen"
              className="h-24 w-auto mb-3 select-none"
              draggable={false}
            />
            <div className="text-[11px] tracking-[0.16em] uppercase text-on-surface/70 font-headline">
              ERP · v2.0 · Internes Portal
            </div>
          </div>

          <div className="text-[17px] font-semibold font-headline text-center mb-5 text-on-surface">
            Wie möchtest du dich anmelden?
          </div>

          {/* ── Staff entry — primary, first ─────────────────────────────── */}
          <GlassCard radius="2xl" className="p-6 mb-4">
            <div className="flex items-center gap-3.5 mb-3">
              <img
                src="/staff-chip.png"
                alt=""
                className="w-9 h-9 rounded-full object-cover flex-shrink-0"
                draggable={false}
              />
              <div className="flex flex-col gap-0.5">
                <span className="text-[10.5px] tracking-[0.14em] uppercase font-medium text-primary">
                  Küche · Spülküche · Ausgabe · Schulküche
                </span>
                <span className="font-headline text-[18px] font-bold text-on-surface leading-none">
                  Chip oder Schulkarte
                </span>
              </div>
            </div>

            <p className="text-[13.5px] leading-relaxed text-on-surface/75 mb-4">
              Anmelden mit deinem Chip oder der Schulkarte.
            </p>

            <button
              onClick={() => window.location.assign('/mitarbeiter/anmelden')}
              className="w-full inline-flex items-center justify-center gap-2.5 h-14 rounded-full
                         bg-primary text-on-primary font-semibold text-[15px]
                         shadow-[0_18px_30px_-12px_rgba(81,95,116,0.55)]
                         transition-transform hover:-translate-y-0.5 active:translate-y-0"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                   strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <circle cx="8" cy="12" r="4.5" />
                <path d="M12.5 12 H21 M18 12 v3.5 M21 12 v2.5" />
              </svg>
              Anmelden
            </button>

            <div className="mt-3 text-center text-[10.5px] tracking-[0.14em] uppercase text-on-surface/60">
              Chip · Schulkarte · dann Geburtsjahr oder PIN
            </div>
          </GlassCard>

          {/* ── Admin/office entry — quieter, second ─────────────────────── */}
          <GlassCard radius="2xl" className="p-6 liquid-glass-fringe">
            <div className="flex items-center gap-3.5 mb-3">
              <span
                className="w-9 h-9 rounded-full flex-shrink-0 flex items-center justify-center bg-surface-container-lowest text-primary shadow-sm"
                aria-hidden="true"
              >
                <span className="material-symbols-outlined text-[20px] leading-none">apartment</span>
              </span>
              <div className="flex flex-col gap-0.5">
                <span className="text-[10.5px] tracking-[0.14em] uppercase font-medium text-on-surface/65">
                  Büro · Leitung · IT
                </span>
                <span className="font-headline text-[18px] font-bold text-on-surface leading-none">
                  Verwaltung
                </span>
              </div>
            </div>

            <p className="text-[13.5px] leading-relaxed text-on-surface/75 mb-4">
              Anmelden mit deinem Microsoft-Konto.
            </p>

            <button
              onClick={() => login()}
              disabled={isLoading}
              className="w-full inline-flex items-center justify-center gap-3 h-[52px] rounded-full font-semibold text-sm text-on-surface transition-transform liquid-glass-pill hover:-translate-y-0.5 active:translate-y-0 disabled:opacity-60"
            >
              <svg width="16" height="16" viewBox="0 0 21 21" aria-hidden="true">
                <rect x="1"  y="1"  width="9" height="9" fill="#f25022"/>
                <rect x="11" y="1"  width="9" height="9" fill="#7fba00"/>
                <rect x="1"  y="11" width="9" height="9" fill="#00a4ef"/>
                <rect x="11" y="11" width="9" height="9" fill="#ffb900"/>
              </svg>
              Mit Microsoft anmelden
            </button>

            <div className="mt-4 flex items-center justify-center gap-2 text-[10.5px] tracking-[0.14em] uppercase text-on-surface/65">
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
              <span>Verschlüsselt · SSO · MFA</span>
            </div>
          </GlassCard>

          {/* Footer status pill */}
          <div className="flex justify-center mt-6">
            <GlassPill className="px-4 py-1.5 text-[10.5px] tracking-[0.14em] uppercase text-on-surface/70">
              © Mammas Canteen · Hamburg
            </GlassPill>
          </div>
        </div>
      </main>

      {/* Corner telemetry — quiet mono chrome per DESIGN.md §8 */}
      <div className="fixed top-4 left-6 z-10 text-[10px] tracking-[0.18em] uppercase text-on-surface/65 font-mono pointer-events-none">
        MC / ERP · 2.0
      </div>
      <div className="fixed top-4 right-6 z-10 text-[10px] tracking-[0.18em] uppercase text-on-surface/65 font-mono pointer-events-none">
        Hamburg · 53.5511° N · 9.9937° E
      </div>
      <div className="fixed bottom-4 left-6 z-10 text-[10px] tracking-[0.18em] uppercase text-on-surface/65 font-mono pointer-events-none">
        Session · Awaiting Auth
      </div>
      <div className="fixed bottom-4 right-6 z-10 text-[10px] tracking-[0.18em] uppercase text-on-surface/65 font-mono pointer-events-none">
        EU-WEST · TLS 1.3
      </div>
    </div>
  )
}
